'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import Image from 'next/image';
import { useLocale } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import parseLocale from '@/core/i18n/parse-locale';
import useBffStockNotificationMutation from '@/core/mutations/bff/use-bff-stock-notification-mutation';
import {
  type EmailAddressSchema,
  emailAddressSchema,
} from '@/core/schemas/email-address-schema';
import { getLocalEmailAddress } from '@/core/services/local-storage/email-address';
import Alert from '@/ui/components/Alert/Alert';
import FlyInPanel from '@/ui/components/FlyInPanel/FlyInPanel';
import { Form } from '@/ui/components/Form/Form';
import GenericInput from '@/ui/components/Form/GenericInput';
import Typography from '@/ui/components/Typography/Typography';
import { useEvaContext } from '@/ui/context/eva';
import { useGA4 } from '@/ui/context/ga4';
import { useNotifyMeFlyInComposition } from '@/ui/context/notify-me-fly-in-composition';

import $ from './NotifyMeFlyIn.module.scss';

type NotifyMeFlyInProps = {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
};

const NotifyMeFlyIn = ({ isOpen, onClose, productId }: NotifyMeFlyInProps) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locale = useLocale();
  const { language: languageCode } = parseLocale(locale);
  const { organization } = useEvaContext();
  const composition = useNotifyMeFlyInComposition();

  const form = useForm<EmailAddressSchema>({
    resolver: zodResolver(emailAddressSchema),
    defaultValues: {
      email: getLocalEmailAddress() || '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const { mutateAsync: createStockNotification, isPending } =
    useBffStockNotificationMutation();

  const { trackOutOfStockSubmit } = useGA4();

  const title = composition?.title ?? '';
  const subtitle = composition?.subtitle ?? '';
  const body = composition?.body ?? '';
  const emailPlaceholder = composition?.emailPlaceholder ?? '';
  const successMessage = composition?.successMessage ?? '';
  const errorMessageFallback = composition?.errorMessage ?? '';

  const imageSrc = composition?.imageUrl ?? '';
  const imageAlt = composition?.imageAlt ?? '';

  const email = form.watch('email');

  const isSubmitDisabled = useMemo(() => {
    if (isPending || isSubmitted) return true;

    // Check if email is valid format
    const isEmailValid =
      email &&
      typeof email === 'string' &&
      email.trim().length > 0 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    return !isEmailValid;
  }, [isPending, isSubmitted, email]);

  const handleSubmit = useCallback(async () => {
    await form.handleSubmit(async (data: EmailAddressSchema) => {
      try {
        setError(null);

        const countryCode = organization?.countryId ?? locale.split('-').pop()?.toUpperCase() ?? 'NL';
        if (!countryCode) {
          throw new Error('Country not found');
        }

        await createStockNotification({
          productId,
          countryCode,
          languageCode,
          emailAddress: data.email,
        });

        trackOutOfStockSubmit();

        setIsSubmitted(true);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : errorMessageFallback,
        );
      }
    })();
  }, [
    form,
    createStockNotification,
    productId,
    organization?.countryId,
    locale,
    languageCode,
    trackOutOfStockSubmit,
    errorMessageFallback,
  ]);

  const handleClose = useCallback(() => {
    form.reset();
    setIsSubmitted(false);
    setError(null);
    onClose();
  }, [form, onClose]);

  return (
    <FlyInPanel
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={handleSubmit}
      submitDisabled={isSubmitDisabled}
      aria-label={title}
      data-t="notify-me-fly-in"
      withHeader={false}
      classNameBottomBar={$.bottomBar}
    >
      <Form {...form}>
        <form
          onSubmit={async e => {
            e.preventDefault();
            await handleSubmit();
          }}
          className={$.content}
        >
          <div className={$.header}>
            <Typography variant="h4" element="h2">
              {title}
            </Typography>

            <Typography variant="bodyMedium" element="p">
              {subtitle}
            </Typography>
          </div>

          {!!imageSrc && (
            <div className={$.imageWrapper}>
              <Image
                src={imageSrc}
                alt={imageAlt}
                fill
                className={$.image}
                sizes="(max-width: 768px) 320px, 640px"
              />
            </div>
          )}

          <Typography variant="h5" element="h3">
            {body}
          </Typography>

          <div className={$.inputRow}>
            <GenericInput
              form={form}
              name="email"
              label={emailPlaceholder}
              placeholder={emailPlaceholder}
              type="email"
              inputMode="email"
              icon={isSubmitted ? 'icon-check' : undefined}
              disabled={isSubmitted}
            />
          </div>
        </form>
        {isSubmitted && (
          <Alert
            type="success"
            message={successMessage}
            className={$.alert}
          />
        )}
        {error && <Alert type="error" message={error} className={$.alert} />}
      </Form>
    </FlyInPanel>
  );
};

export default NotifyMeFlyIn;
